import React, { useState } from "react";
import { TitleBlock, Note, Actions, PrimaryBtn, SecondaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { ScoreCard, Section, RowList, Spinner, fmtK, visibilityRead, confidenceLabel, EMPTY } from "../onboardingPieces";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import logoIg from "../../../../../assets/brand/onboarding/logo-instagram-22.svg";
import logoTt from "../../../../../assets/brand/onboarding/logo-tiktok-22.svg";
import logoYt from "../../../../../assets/brand/onboarding/logo-youtube-22.svg";
import logoX from "../../../../../assets/brand/onboarding/logo-x-22.svg";
import checkBig from "../../../../../assets/brand/onboarding/check-12x9.svg";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", placeholder: "instagram.com/username", match: /instagram\.com\//i, logo: logoIg },
  { key: "tiktok", label: "TikTok", placeholder: "tiktok.com/@username", match: /tiktok\.com\//i, logo: logoTt },
  { key: "youtube", label: "YouTube", placeholder: "youtube.com/@channel", match: /youtu\.?be/i, logo: logoYt },
  { key: "x", label: "X", placeholder: "x.com/username", match: /(x\.com|twitter\.com)\//i, logo: logoX },
];

const linksByPlatform = (urls = []) => {
  const out = {};
  PLATFORMS.forEach((p) => { out[p.key] = urls.find((u) => p.match.test(u)) || ""; });
  return out;
};

// One social row (Figma "SocialRow"): 22px logo and overline, a 56px field
// that turns green with a check once the profile was read.
const SocialRow = ({ p, value, onChange, found, fetching, onNotMe }) => (
  <div className="flex flex-col gap-[10px]">
    <div className="flex h-[43px] items-center gap-2"><img src={p.logo} alt="" className="h-[22px] w-[22px]" /><span className="bm-overline">{p.label}</span></div>
    <div className={`flex h-14 items-center gap-[10px] rounded-[12px] bg-white pl-[18px] pr-4 ${
      found ? "border-2 border-bm-positive" : fetching ? "border-2 border-bm-ink" : "border border-bm-border focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]"
    }`}>
      <input className="ob-bare min-w-0 flex-1" value={value} placeholder={p.placeholder} onChange={(e) => onChange(e.target.value)} aria-label={`${p.label} link`} inputMode="url" autoCapitalize="none" autoCorrect="off" />
      {found && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-bm-positive"><img src={checkBig} alt="" className="h-[9px] w-3" /></span>}
      {fetching && <Spinner size={24} />}
    </div>
    {found && found.handle && (
      <div className="flex h-[124px] items-center gap-[10px] rounded-[8px] bg-bm-subtle px-[14px] py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e1306c] bm-label-xs text-white">{found.initials}</span>
        <div className="flex min-w-0 flex-1 flex-col gap-px"><span className="bm-label-l text-bm-ink">{found.name}</span><span className="bm-body-s text-bm-muted">{fmtK(found.followers)} followers · public account</span></div>
        <button type="button" onClick={onNotMe} className="bm-label-xs text-bm-orange hover:underline">Not me</button>
      </div>
    )}
    {fetching && <p className="bm-body-s text-bm-muted">Looking up the profile…</p>}
  </div>
);

// 06 · Paste your profile links.
export const SocialLinksStep = ({ state, onSave }) => {
  const [links, setLinks] = useState(() => linksByPlatform(state.socialMedia));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const filled = PLATFORMS.filter((p) => (links[p.key] || "").trim());
  const platforms = state.social?.platforms || {};
  const name = `${state.firstName || ""} ${state.lastName || ""}`.trim();
  const initials = `${(state.firstName || "")[0] || ""}${(state.lastName || "")[0] || ""}`.toUpperCase();

  const validate = () => {
    for (const p of PLATFORMS) {
      const v = (links[p.key] || "").trim();
      if (v && /\s/.test(v)) return `Remove the spaces from your ${p.label} link.`;
      if (v && !p.match.test(v)) return `That does not look like a ${p.label} link.`;
    }
    return "";
  };
  const save = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    if (filled.length === 0) { setError("Paste at least one link, or skip for now."); return; }
    setBusy(true); setError("");
    try { await onSave(filled.map((p) => links[p.key].trim())); } catch (e) { setError(e.message || "We could not save your links."); } finally { setBusy(false); }
  };

  return (
    <>
      <TitleBlock eyebrow="Social" title="Paste your profile links" sub="We read reach and engagement from public profiles. This builds your visibility score." />
      <div className="flex flex-col gap-[18px]">
        {PLATFORMS.map((p) => {
          const pm = platforms[p.key];
          const found = pm && (links[p.key] || "").trim() && (state.socialMedia || []).includes((links[p.key] || "").trim()) ? { name, initials, followers: pm.followers, handle: pm.handle || name } : null;
          return <SocialRow key={p.key} p={p} value={links[p.key]} onChange={(v) => setLinks({ ...links, [p.key]: v })} found={found} onNotMe={() => setLinks({ ...links, [p.key]: "" })} />;
        })}
      </div>
      <Note>Accounts must be public for us to read them. No password or login is needed.</Note>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={save} loading={busy}>Continue</PrimaryBtn>
      </Actions>
    </>
  );
};

// 07 · Importing. Phones (1579:853): the 96px Spinner LG, 80px lower, and
// 24px between every block.
export const SocialImportingStep = ({ platformLabel = "your socials" }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className="flex flex-col items-center gap-6 pt-20 text-center">
        <Spinner size={96} />
        <h1 className="bm-h-l text-bm-ink">Reading {platformLabel}</h1>
        <p className="bm-body-l text-bm-muted">Cache is importing your reach and engagement and building your visibility score.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-6 pt-16 text-center">
      <Spinner />
      <div className="flex flex-col gap-3">
        <h1 className="bm-h-l text-bm-ink">Reading {platformLabel}</h1>
        <p className="mx-auto max-w-[420px] bm-body-l text-bm-muted">Cache is importing your reach and engagement and building your visibility score.</p>
      </div>
    </div>
  );
};

// The status card's platform on phones, from the first saved link (label,
// tile colour and code as in the edit hub's socials); any other link keeps
// the generic card.
const FAILED_TILE = {
  instagram: { label: "Instagram", code: "IG", tile: "bg-[#e1306c]" },
  tiktok: { label: "TikTok", code: "TT", tile: "bg-bm-ink" },
  youtube: { label: "YouTube", code: "YT", tile: "bg-[#ff0000]" },
  x: { label: "X", code: "X", tile: "bg-bm-ink" },
};
const failedPlatform = (url) => {
  const p = PLATFORMS.find((q) => q.match.test(url || ""));
  return (p && FAILED_TILE[p.key]) || { label: "Social profile", code: "IG", tile: "bg-[#e1306c]" };
};

const FAILED_TIPS = [
  "Switch to a public, Business or Creator account and retry",
  "Connect a different network instead",
  "Continue without it. You will not have a full CacheScore until a social account is read",
];

// 06a · Could not connect. Phones (1579:953, D-o7): the SocialRow status
// card (a 40px platform tile, the platform in bm-h-s, a 40px Connect) and
// dot bullets on 10px rows.
export const SocialFailedStep = ({ state, onRetry, onContinue }) => {
  const phone = usePhoneTheme();
  const first = (state.socialMedia || [])[0] || "";
  const handle = first.replace(/^https?:\/\/(www\.)?/, "");
  const platform = failedPlatform(first);
  return (
    <>
      <TitleBlock tone="critical" eyebrow="Could not connect" title="We could not read your account" sub="A private account will not share follower or engagement data. Public Business or Creator accounts read best." />
      {phone ? (
        <div className="flex items-center justify-between gap-3 rounded-[12px] border border-bm-border bg-white px-4 py-[14px]">
          <div className="flex min-w-0 items-center gap-[14px]">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] ${platform.tile} bm-label-l text-white`}>{platform.code}</span>
            <div className="flex min-w-0 flex-col gap-[3px] break-words"><div className="bm-h-s text-bm-ink">{platform.label}</div><div className="bm-body-m text-bm-muted">{handle || "no link"} · could not read</div></div>
          </div>
          <button type="button" onClick={onRetry} className="flex h-10 shrink-0 items-center rounded-full border border-bm-border bg-white px-[18px] bm-label-l text-bm-ink hover:bg-bm-subtle">Connect</button>
        </div>
      ) : (
        <div className="flex items-center justify-between rounded-[12px] border border-bm-border bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-[#e1306c] bm-label-xs text-white">IG</span>
            <div><div className="bm-label-l text-bm-ink">Social profile</div><div className="bm-body-s text-bm-muted">{handle || "no link"} · could not read</div></div>
          </div>
          <button type="button" onClick={onRetry} className="flex h-9 items-center rounded-full border border-bm-border bg-white px-4 bm-label-l text-bm-ink hover:bg-bm-subtle">Connect</button>
        </div>
      )}
      {phone ? (
        <div className="flex flex-col">
          <span className="bm-overline">What you can do</span>
          <ul className="flex flex-col bm-body-m text-bm-muted">
            {FAILED_TIPS.map((tip) => (
              <li key={tip} className="flex gap-[10px] py-[10px]"><span aria-hidden="true" className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-bm-tertiary" />{tip}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-[10px]">
          <span className="bm-overline">What you can do</span>
          <ul className="flex flex-col gap-[10px] bm-body-m text-bm-muted">
            {FAILED_TIPS.map((tip) => (
              <li key={tip} className="flex gap-2"><span className="text-bm-tertiary">•</span>{tip}</li>
            ))}
          </ul>
        </div>
      )}
      <Actions>
        <PrimaryBtn onClick={onRetry}>Try again</PrimaryBtn>
        <SecondaryBtn onClick={onContinue}>Continue without social</SecondaryBtn>
      </Actions>
    </>
  );
};

const PARTIAL_COSTS = [
  { title: "Discoverability", sub: "Most searches filter on complete scores" },
  { title: "NILx Rate Card", sub: "Pricing needs social media data to calculate" },
  { title: "Atlas matching", sub: "Opportunities are matched on reach as well as play" },
];

// 06b · Skipped socials, partial score. Phones (1580:977): the compact card
// with the confidence badge, the halves card with the warning on
// Visibility, and the grey cost list with sentence-case badges and the 24px
// triangle.
export const SocialPartialStep = ({ state, onConnect, onContinue }) => {
  const phone = usePhoneTheme();
  return (
    <>
      <ScoreCard
        score={state.metrics?.cacheScore || 0}
        ringLabel={(state.metrics?.cacheScore || 0) < 50 ? "low" : "of 100"}
        title="CacheScore™"
        sub="Performance is in. Connect a social account to add visibility."
        confidence={confidenceLabel(state)}
        visibility={{ state: "warn", note: "Not connected", alert: true }}
        performance={{ state: state.performance?.source ? "on" : "warn", note: state.performance?.source ? "From team data" : "Awaiting stats" }}
      />
      <Section label="What a partial score costs">
        {phone
          ? <RowList tone="subtle" textWidth={200} badgeUpper={false} rows={PARTIAL_COSTS.map((r) => ({ ...r, badge: "Limited", alert: true }))} />
          : <RowList rows={PARTIAL_COSTS.map((r) => ({ ...r, badge: "Limited", warn: true }))} />}
      </Section>
      <Note wide>Nothing is lost permanently. Connect an account any time and the visibility half fills in immediately.</Note>
      <Actions>
        <PrimaryBtn onClick={onConnect}>Connect an account</PrimaryBtn>
        <TextBtn onClick={onContinue}>Continue with performance only</TextBtn>
      </Actions>
    </>
  );
};

// 08 · Visibility captured. `pending`: the accounts were still being read
// when the wait ran out, so nothing is claimed yet. Nothing is claimed either
// while the state shows nothing read. Phones (1676:1109): the compact card
// with the confidence badge and the halves card.
export const SocialCapturedStep = ({ state, onNext, pending: waiting = false }) => {
  const pending = waiting || !visibilityRead(state);
  const s = state.social || {};
  const platforms = Object.entries(s.platforms || {});
  const subParts = platforms.map(([k, v]) => `${k[0].toUpperCase()}${k.slice(1)} ${fmtK(v.followers)}`).join(" · ");
  const perf = !!state.performance?.source;
  // No number on the ring yet, so no confidence badge beside it either.
  const muted = pending && !(state.metrics?.cacheScore > 0);
  return (
    <>
      <ScoreCard
        score={state.metrics?.cacheScore || Math.round(s.score || 0)}
        muted={muted}
        title={pending ? "Reading your accounts" : perf ? "Your CacheScore is live" : "Partial score"}
        sub={pending ? "This can take a few minutes. Your visibility score shows on your profile once it is ready."
          : perf ? "Both halves are in, visibility from your socials, performance from your team." : "Visibility is in. Performance is waiting on game data from your team."}
        confidence={muted ? null : confidenceLabel(state)}
        visibility={pending ? { state: "warn", note: "Reading your accounts" } : { state: "on", note: "From your socials" }}
        performance={{ state: perf ? "on" : "warn", note: perf ? "From team data" : "Awaiting stats" }}
      />
      {!pending && (
        <Section label="What we read">
          <RowList rows={[
            { title: "Total followers", sub: subParts || "Across your connected accounts", value: fmtK(s.totalFollowers) },
            { title: "Engagement rate", sub: "vs 3.4% benchmark", value: s.engagementRate != null ? `${(Number(s.engagementRate) * (s.engagementRate < 1 ? 100 : 1)).toFixed(1)}%` : EMPTY },
            { title: "Posts analysed", sub: "Last 12 months", value: s.postsAnalysed != null ? String(s.postsAnalysed) : EMPTY },
          ]} />
        </Section>
      )}
      <Note wide>Add your team next and the performance half calculates too. That is what completes your CacheScore.</Note>
      <Actions><PrimaryBtn onClick={onNext}>Add my team</PrimaryBtn></Actions>
    </>
  );
};
